import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';

import useAuthStore from 'store/auth';
import { DashboardDetail } from 'services/dashboard/type';
import { PATH_ROUTES } from 'routes/path';

import ReportIcon from 'assets/images/dashboard/report-icon.png';
import FirstAid from 'assets/images/dashboard/first-aid-icon.png';
import {
  getRecommendationImg,
  getRecommendationName,
} from 'lib/report-recommendations';

export const getRecommendations = (
  steps: string[] | Record<string, string> | undefined
) => {
  if (!steps) return [];

  let nextSteps = [];

  if (Array.isArray(steps)) {
    nextSteps = steps.map((step) => {
      if (step.includes(':')) {
        const [key, value] = step.split(':');
        const name = key.replace(/^\d+\.\s*/, '');

        return {
          name: name,
          img: getRecommendationImg(name),
          desc: value.trimStart(),
        };
      }

      const name = getRecommendationName(step) || 'Test & Monitor';

      return {
        name,
        img: getRecommendationImg(name),
        desc: step,
      };
    });
  } else {
    nextSteps = Object.entries(steps)?.map(([key, value]) => ({
      name: key,
      img: getRecommendationImg(key),
      desc: value,
    }));
  }

  return nextSteps.slice(0, 3);
};

const Recommendations = ({
  recommendValue,
  showOverlay,
}: {
  recommendValue: 'AI_REPORT' | 'LAB_REPORT';
  showOverlay: boolean;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [selectedRecommendation, setSelectedRecommendation] =
    useState<number>();

  const dashboardDetails =
    (queryClient.getQueryData([
      'dashboard-flags',
      profileId,
    ]) as DashboardDetail) || null;

  const recommendations = {
    AI_REPORT: {
      label: t('health_report_recommendations'),
      background:
        'linear-gradient(146.95deg, rgba(172, 183, 250, 0.24) 10.41%, rgba(93, 116, 255, 0.32) 84.68%)',
      icon: ReportIcon,
      borderColor: '#ACB7FA',
      recommendations: getRecommendations(
        dashboardDetails?.personalized_report?.ai_insights?.prevention
      ),
      noScanInfo: t('get_health_report'),
      generateLink: PATH_ROUTES.GENERATE_AI_REPORT,
    },
    LAB_REPORT: {
      label: t('lab_report_recommendations'),
      background:
        'linear-gradient(146.95deg, rgba(81, 232, 241, 0.24) 10.41%, rgba(49, 186, 194, 0.32) 84.68%)',
      icon: FirstAid,
      borderColor: '#4CB7D8',
      recommendations: getRecommendations(
        dashboardDetails?.lab_report?.ai_insights?.test_and_next_step
      ),
      noScanInfo: t('get_lab_report'),
      generateLink: PATH_ROUTES.GENERATE_LAB_REPORT,
    },
  };

  const recommend =
    recommendations[recommendValue as keyof typeof recommendations];

  return (
    <div
      className='relative h-full px-4 py-2 border border-t-4 shadow-md rounded-2xl min-h-32'
      style={{ borderTopColor: recommend.borderColor }}
    >
      {showOverlay ? (
        <div className='absolute -top-2 left-0 grid w-full h-[105%] bg-white/50 backdrop-blur-md rounded-2xl place-items-center z-50'>
          <Link
            to={recommend.generateLink}
            style={{ background: recommend.borderColor }}
            className='flex items-center h-8 px-4 font-medium text-white rounded-3xl hover:opacity-90'
          >
            <span>{recommend.noScanInfo}</span>
            <ChevronRight className='ml-2 size-4' />
          </Link>
        </div>
      ) : (
        <div className='absolute w-6 h-6 right-4 top-2'>
          <img
            src={recommend.icon}
            alt={`${recommend.label} image`}
            className='object-contain w-full h-full'
          />
        </div>
      )}

      <h2 className='mb-2 mr-8 font-semibold leading-none'>
        {recommend.label}
      </h2>

      <AnimatePresence>
        {typeof selectedRecommendation === 'number' ? (
          <motion.div
            className='absolute top-0 left-0 w-full h-full p-5 bg-white'
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 30,
            }}
          >
            <div className='flex items-start gap-2'>
              <button onClick={() => setSelectedRecommendation(undefined)}>
                <ChevronLeft className='w-4 h-4' />
              </button>

              <div className='flex items-center gap-4'>
                <div
                  className='flex flex-col items-center justify-center gap-2 p-2 cursor-pointer w-36 rounded-2xl shrink-0'
                  style={{ background: recommend.background }}
                >
                  <div className='flex items-center h-8'>
                    <p className='px-4 text-sm font-semibold leading-none text-center capitalize'>
                      {recommend.recommendations[selectedRecommendation].name}
                    </p>
                  </div>
                  <div className='min-w-full min-h-full size-12'>
                    <img
                      src={
                        recommend.recommendations[selectedRecommendation].img
                      }
                      alt={
                        recommend.recommendations[selectedRecommendation].name
                      }
                      className='object-contain w-full h-full'
                    />
                  </div>
                </div>

                <motion.p
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 1 }}
                  className='text-sm leading-none whitespace-pre-wrap'
                >
                  {recommend.recommendations[selectedRecommendation].desc}
                </motion.p>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className='grid h-[calc(100%-40px)] grid-cols-3 gap-4'>
            {recommend.recommendations.map(({ name, img }, index) => (
              <motion.div
                className='flex flex-col items-center justify-center gap-2 p-2 cursor-pointer rounded-2xl'
                style={{ background: recommend.background }}
                key={name}
                onClick={() => setSelectedRecommendation(index)}
                initial={showOverlay ? {} : { opacity: 0, y: 40 }}
                animate={showOverlay ? {} : { opacity: 1, y: 0 }}
                transition={{ ease: 'easeOut' }}
              >
                <div className='flex items-center h-8'>
                  <p className='px-4 text-sm font-semibold leading-none text-center capitalize'>
                    {name}
                  </p>
                </div>
                <div className='size-12'>
                  <img
                    src={img}
                    alt={name}
                    className='object-contain w-full h-full'
                  />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(Recommendations);
